import { noop } from 'lodash';
import { VFC } from 'react';

import CaseConditionalLogicEditor, {
  DEFAULT_MODULE_VISIBILITY_CONDITIONAL,
} from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor';
import { CasesCustomCodeInput } from 'dashboard/apps/DashboardLayoutEditor/ComputedPropertyInput/CustomCodeInput';
import { TemplateLiteralInput } from 'dashboard/apps/DashboardLayoutEditor/ComputedPropertyInput/TemplateLiteralInput';
import {
  CaseTemplateConditional,
  ComputedProperty,
} from 'dashboard/lib/case-layouts/computed-properties';
import { JsonSchema } from 'lib/json-schema';

interface ComputedPropertyInputProps {
  computedProperty: ComputedProperty;
  dataSchema: JsonSchema;
  keyPrefix?: string;
  onComputedPropertyUpdate: (computedProperty: ComputedProperty) => void;
  setComputedPropertyValid?: (valid: boolean) => void;
}

const ComputedPropertyInput: VFC<ComputedPropertyInputProps> = ({
  computedProperty,
  dataSchema,
  onComputedPropertyUpdate,
  setComputedPropertyValid = noop,
  keyPrefix = '',
}) => {
  // In the future when we have more resolution mechanisms, this function will figure out what input to render
  switch (computedProperty['resolution-mechanism']['resolution-type']) {
    case 'custom-code': {
      const customCode = computedProperty['resolution-mechanism'];
      const handleSerializedCodeUpdate = (serializedCode: string) => {
        onComputedPropertyUpdate({
          ...computedProperty,
          'resolution-mechanism': {
            'resolution-type': 'custom-code',
            'serialized-code': serializedCode,
          },
        });
      };

      return (
        <CasesCustomCodeInput
          dataSchema={dataSchema}
          customCode={customCode}
          onSerializedCodeUpdate={handleSerializedCodeUpdate}
          returnType={computedProperty['return-type']}
        />
      );
    }
    case 'conditional-logic': {
      const updateConditional = (conditional: CaseTemplateConditional) => {
        onComputedPropertyUpdate({
          ...computedProperty,
          'resolution-mechanism': {
            'resolution-type': 'conditional-logic',
            conditional,
          },
        });
      };

      return (
        <CaseConditionalLogicEditor
          conditional={
            computedProperty['resolution-mechanism']['conditional'] ??
            DEFAULT_MODULE_VISIBILITY_CONDITIONAL
          }
          jsonSchema={dataSchema}
          keyPrefix={keyPrefix}
          updateConditional={updateConditional}
          setConditionalValidity={setComputedPropertyValid}
        />
      );
    }
    case 'template-literal': {
      const templateLiteral = computedProperty['resolution-mechanism'];
      const handleSerializedCodeUpdate = (serializedCode: string) => {
        onComputedPropertyUpdate({
          ...computedProperty,
          'resolution-mechanism': {
            'resolution-type': 'template-literal',
            'serialized-code': serializedCode,
          },
        });
      };

      return (
        <TemplateLiteralInput
          templateLiteral={templateLiteral}
          onSerializedCodeUpdate={handleSerializedCodeUpdate}
        />
      );
    }
    default: {
      return null;
    }
  }
};

export default ComputedPropertyInput;
